import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Eye, Download, History, Trash2, Edit, FileText, Search, X as XIcon } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../../common/axiosInstance";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import DocumentUploadModal from "./DocumentUploadModal";
import DocumentVersionHistory from "./DocumentVersionHistory";
import { S3TempUrl } from "../../../utils/s3Client";
import { DesignDocument, DocumentVersion, DesignFolder } from "./DesignDocumentationIntegration";
import { getUserDirectory } from "../../../service/api";
import { useAuth } from "../../../auth/AuthProvider";

const DesignFolderView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedRegion, selectedPremises, folder: folderState } = location.state || {};
  
  const [folder, setFolder] = useState<DesignFolder | null>(folderState || null);
  const [documents, setDocuments] = useState<DesignDocument[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DesignDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("all");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DesignDocument | null>(null);
  const [fileurl, setFileUrl] = useState<{ url: string; name: string }>({
    url: "",
    name: "",
  });
  const [resMessage, setResMessage] = useState<string>("");
  const [userNamesMap, setUserNamesMap] = useState<{ [key: string]: string }>({});
  const { getsignedurl } = S3TempUrl();
  const { user: currentUser } = useAuth(); // Get current logged-in user (could be org admin or org user)

  useEffect(() => {
    if (id) {
      fetchFolderData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch users to map uploadedBy IDs to names (handles both org users and org admins)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Create a map of user IDs to names
        const namesMap: { [key: string]: string } = {};
        
        // 1. Fetch Org Users (from /api/orgUser/directory)
        // Response structure: { data: [...org users...], success: true, message: "..." }
        // Org User structure: { _id: "...", firstName: "...", lastName: "...", email: "...", roles: ["orguser"] }
        try {
          const orgUsersResponse = await getUserDirectory();
          const orgUsersData = orgUsersResponse.data?.data || [];
          const orgUsers = Array.isArray(orgUsersData) ? orgUsersData : [];
          
          orgUsers.forEach((user: any) => {
            // Org User uses _id (not id)
            const userId = (user._id || user.id || "").toString();
            if (!userId) return;
            
            // Build name from firstName and lastName
            const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || 
                           user.email || 
                           user.username || 
                           userId;
            
            // Store with _id (primary) and id (fallback)
            namesMap[userId] = userName;
            if (user._id) namesMap[user._id.toString()] = userName;
            if (user.id && user.id !== user._id) namesMap[user.id.toString()] = userName;
          });
        } catch (error) {
          console.error("Error fetching org users:", error);
        }
        
        // 2. Add current logged-in org admin (if applicable) from auth context
        // Org Admin structure: { id: "...", org_admin_name: "...", username: "...", email: "...", roles: ["orgadmin"] }
        if (currentUser) {
          // Type assertion to handle both org admin and org user structures
          const user = currentUser as any;
          const currentUserId = (user.id || user._id || "").toString();
          if (currentUserId) {
            let currentUserName = "";
            
            // Check if current user is org admin
            if (user.org_admin_name) {
              // Org Admin structure: has org_admin_name, id (not _id)
              currentUserName = user.org_admin_name || 
                              user.username || 
                              user.email || 
                              currentUserId;
              namesMap[currentUserId] = currentUserName;
              if (user.id) namesMap[user.id.toString()] = currentUserName;
              if (user._id) namesMap[user._id.toString()] = currentUserName;
            } else if (user.firstName || user.lastName) {
              // Org User structure: has firstName, lastName, _id
              currentUserName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || 
                              user.email || 
                              user.username || 
                              currentUserId;
              namesMap[currentUserId] = currentUserName;
              if (user._id) namesMap[user._id.toString()] = currentUserName;
              if (user.id) namesMap[user.id.toString()] = currentUserName;
            }
          }
        }
        
        setUserNamesMap(namesMap);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [currentUser]);

  useEffect(() => {
    if (id && (searchQuery || documentTypeFilter !== "all")) {
      // Refetch with filters when search or filter changes
      fetchFolderData();
    } else {
      // Just filter local documents when no server-side filtering needed
      filterDocuments();
    }
  }, [searchQuery, documentTypeFilter]);

  // Update filtered documents when documents change (e.g., after upload)
  // This ensures the table updates when documents are fetched
  useEffect(() => {
    filterDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const fetchFolderData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const params: { [key: string]: any } = {};
      if (searchQuery.trim()) params.keyword = searchQuery;
      if (documentTypeFilter && documentTypeFilter !== "all") params.documentType = documentTypeFilter;

      const response = await axiosInstance.get(`/api/utility-systems/design-folders/${id}`, { params });
      
      if (response.data.success && response.data.resCode === 200) {
        const folderData = response.data.data || response.data.message;
        // Ensure folder has both _id and id
        const folderWithIds = {
          ...folderData,
          id: folderData.id || folderData._id,
          _id: folderData._id || folderData.id,
        };
        setFolder(folderWithIds);
        
        // Documents are in the files array, ensure they have proper IDs and signedUrls
        const documentsWithIds = (folderData.files || []).map((doc: any) => ({
          ...doc,
          id: doc.id || doc._id,
          _id: doc._id || doc.id,
          folderId: folderData._id || folderData.id,
          signedUrl: doc.signedUrl, // Use signedUrl from backend
        }));
        setDocuments(documentsWithIds);
        
        // Update filtered documents immediately after setting documents
        let filtered = [...documentsWithIds];
        if (searchQuery.trim()) {
          filtered = filtered.filter(
            (doc) =>
              doc.documentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
              doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              doc.fileName.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        if (documentTypeFilter !== "all") {
          filtered = filtered.filter((doc) => doc.documentType === documentTypeFilter);
        }
        setFilteredDocuments(filtered);
      }
    } catch (error: any) {
      console.error("Error fetching folder:", error);
      if (error.response?.status === 404) {
        toast.error("Folder not found");
      } else {
        toast.error(error.response?.data?.resMessage || "Failed to load folder");
      }
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    if (searchQuery) {
      filtered = filtered.filter(
        (doc) =>
          doc.documentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.fileName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (documentTypeFilter !== "all") {
      filtered = filtered.filter((doc) => doc.documentType === documentTypeFilter);
    }

    setFilteredDocuments(filtered);
  };

  const handleUploadSuccess = async () => {
    // Refresh folder data to show new document immediately in table
    await fetchFolderData();
    setIsUploadModalOpen(false);
    toast.success("Document uploaded successfully");
  };

  const handleView = useCallback(async (document: DesignDocument) => {
    try {
      // Use originalName if available, otherwise fall back to name - exactly like Policies and Manuals
      const fileName = (document as any).originalName || document.fileName;
      // Get file type from fileName extension
      const fileExtension = document.fileName.split('.').pop()?.toLowerCase() || '';
      const mimeTypes: { [key: string]: string } = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'txt': 'text/plain',
      };
      const fileType = mimeTypes[fileExtension] || 'application/octet-stream';
      
      // Use signedUrl from backend if available, otherwise generate one
      let fileUrl = (document as any).signedUrl;
      if (!fileUrl) {
        fileUrl = await getsignedurl(fileName, fileType);
      }
      const filobj = {
        url: fileUrl,
        name: document.fileName, // Use fileName (with extension) for file type detection, exactly like Policies and Manuals
      };
      setFileUrl(filobj);
    } catch (err: any) {
      setResMessage("File not found");
    }
  }, [getsignedurl]);

  const handleViewVersion = async (document: DesignDocument, version: DocumentVersion) => {
    try {
      setSelectedDocument(document);
      // Use signedUrl from backend if available, otherwise generate one
      let fileUrl = (version as any).signedUrl;
      if (!fileUrl && version.fileUrl) {
        // Extract S3 key from fileUrl
        let s3Key = version.fileUrl;
        if (version.fileUrl.includes("amazonaws.com/")) {
          s3Key = version.fileUrl.split("amazonaws.com/")[1];
        } else if (version.fileUrl.startsWith("s3://")) {
          s3Key = version.fileUrl.replace("s3://", "").split("/").slice(1).join("/");
        }
        
        // Get file type from fileName extension
        const fileExtension = version.fileName.split('.').pop()?.toLowerCase() || '';
        const mimeTypes: { [key: string]: string } = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'ppt': 'application/vnd.ms-powerpoint',
          'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'txt': 'text/plain',
        };
        const fileType = mimeTypes[fileExtension] || 'application/octet-stream';
        
        fileUrl = await getsignedurl(s3Key || version.fileName, fileType);
      } else if (!fileUrl) {
        toast.error("File URL not available");
        return;
      }
      const filobj = {
        url: fileUrl,
        name: version.fileName, // Use fileName (with extension) for file type detection, exactly like Policies and Manuals
      };
      setFileUrl(filobj);
    } catch (error) {
      console.error("Error viewing version:", error);
      toast.error("Failed to load document version");
    }
  };

  const handleVersionHistory = (document: DesignDocument) => {
    setSelectedDocument(document);
    setIsVersionHistoryOpen(true);
  };

  const handleDelete = async (document: DesignDocument) => {
    if (!window.confirm(`Are you sure you want to delete "${document.documentName}"?`)) {
      return;
    }

    try {
      const docId = document._id || document.id;
      const folderId = id || document.folderId;
      if (!docId || !folderId) {
        toast.error("Document ID or Folder ID not found");
        return;
      }

      const response = await axiosInstance.delete(`/api/utility-systems/design-folders/${folderId}/documents/${docId}`);
      
      if (response.data.success && response.data.resCode === 200) {
        toast.success(response.data.resMessage || "Document deleted successfully");
        fetchFolderData();
      } else {
        toast.error(response.data.resMessage || "Failed to delete document");
      }
    } catch (error: any) {
      console.error("Error deleting document:", error);
      toast.error(error.response?.data?.resMessage || error.response?.data?.message || "Failed to delete document");
    }
  };

  const handleDownload = async (document: DesignDocument) => {
    try {
      // Use signedUrl from backend if available, otherwise generate one
      let fileUrl = (document as any).signedUrl;
      if (!fileUrl) {
        fileUrl = await getsignedurl(document.fileName, document.fileUrl);
      }
      const link = window.document.createElement("a");
      link.href = fileUrl;
      link.download = document.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success("Download started");
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      architectural_drawing: "Architectural Drawing",
      blueprint: "Blueprint",
      permit: "Permit",
      other: "Other",
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="px-2 py-4 w-full h-full flex justify-center items-center">
        <SpinLoader />
      </div>
    );
  }

  if (!folder) {
    return (
      <div className="px-2 py-4 w-full h-full mt-10">
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <p className="text-gray-600">Folder not found</p>
          <button
            onClick={() => navigate("/utility-systems/design-documentation-integration")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Folders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 py-4 w-full h-full mt-10">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/utility-systems/design-documentation-integration")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Folders
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{folder.folderName}</h1>
            {folder.description && (
              <p className="text-gray-600 mt-1">{folder.description}</p>
            )}
          </div>
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </div>
          <div className="min-w-[180px]">
            <select
              value={documentTypeFilter}
              onChange={(e) => setDocumentTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="architectural_drawing">Architectural Drawings</option>
              <option value="blueprint">Blueprints</option>
              <option value="permit">Permits</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-sm">
        {filteredDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-500">
            <FileText className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No documents found</p>
            <p className="text-sm">
              {documents.length === 0
                ? "Upload your first document to this folder"
                : "Try adjusting your search or filters"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-3 px-4 font-semibold text-gray-700">Document Name</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Version</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Uploaded Date</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Uploaded By</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((document) => {
                  const typeLabel = getDocumentTypeLabel(document.documentType);
                  let bgColor = "bg-blue-100 text-blue-800";
                  if (document.documentType === "architectural_drawing") bgColor = "bg-purple-100 text-purple-800";
                  else if (document.documentType === "blueprint") bgColor = "bg-indigo-100 text-indigo-800";
                  else if (document.documentType === "permit") bgColor = "bg-green-100 text-green-800";

                  return (
                    <tr key={document._id || document.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="font-medium text-gray-900">{document.documentName}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${bgColor}`}>
                          {typeLabel}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-semibold">
                          v{document.currentVersion || 1}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {document.uploadedAt
                          ? new Date(document.uploadedAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "-"}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {(() => {
                          const uploadedById = (document.uploadedBy || "").toString();
                          if (!uploadedById) return "-";
                          
                          // Try to find in userNamesMap with different ID formats
                          let userName = userNamesMap[uploadedById];
                          
                          // If not found, try case-insensitive match and different formats
                          if (!userName) {
                            const foundKey = Object.keys(userNamesMap).find(
                              key => key.toString().toLowerCase() === uploadedById.toLowerCase()
                            );
                            if (foundKey) {
                              userName = userNamesMap[foundKey];
                            }
                          }
                          
                          // Return readable name if found
                          if (userName && userName !== uploadedById) {
                            return userName;
                          }
                          
                          // If uploadedBy looks like an ObjectId (24 char hex string), show "-"
                          // Otherwise, if it's already a readable string, show it
                          if (/^[0-9a-fA-F]{24}$/.test(uploadedById)) {
                            return "-"; // It's an ObjectId but not in our map yet
                          }
                          
                          // If it doesn't look like an ObjectId, it might already be a name
                          return uploadedById || "-";
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleView(document)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleVersionHistory(document)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Version History"
                          >
                            <History className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDownload(document)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(document)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && id && (
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onSuccess={handleUploadSuccess}
          existingDocument={null}
          folderId={id}
        />
      )}

      {/* Version History Modal */}
      {isVersionHistoryOpen && selectedDocument && (
        <DocumentVersionHistory
          isOpen={isVersionHistoryOpen}
          onClose={() => {
            setIsVersionHistoryOpen(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          onViewVersion={handleViewVersion}
        />
      )}

      {/* Document Viewer - Exactly like Policies and Manuals */}
      {fileurl.url !== "" && (
        <div
          className="absmiddle"
          style={{
            width: "100vw",
            height: "100vh",
            zIndex: 999999,
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <XIcon
            className="absolute left-auto top-2 z-10 cursor-pointer bg-red-500 text-white"
            style={{ right: "115px" }}
            onClick={() => setFileUrl({ url: "", name: "" })}
          />
          <div
            className="absmiddle bg-white border py-9"
            style={{
              width: "calc(100vw - 200px)",
              height: "100vh",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <DocumentViewer file={fileurl.url} name={fileurl.name} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignFolderView;

